import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_V1, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import Modal from '../components/ui/Modal';
import KpiGrid from '../components/ui/KpiGrid';

const JOBS_API = `${API_V1}/admin/scheduled-jobs`;

interface JobRow {
  job_key: string;
  description: string;
  owner_phase: string;
  per_org: boolean;
  default_enabled: boolean;
  handler_is_stub: boolean;
  cadence_seconds: number;
  enabled: boolean | null;
  last_run_at: string | null;
  next_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  consecutive_failures: number;
  // Phase 16k ITGC change-management fields
  pending_approval?: boolean;
  enabled_admin_overridden_at?: string | null;
  last_change_ticket_ref?: string | null;
}

interface RunRow {
  id: number;
  org_id: number | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  status: string;
  result: Record<string, unknown> | null;
  error_trace: string | null;
  trigger_source: string;
}

const STATUS_COLOR: Record<string, string> = {
  SUCCESS: 'var(--positive)',
  FAILED: 'var(--negative)',
};

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 0) {
    const abs = -diff;
    if (abs < 60) return `in ${abs}s`;
    if (abs < 3600) return `in ${Math.floor(abs / 60)}m`;
    if (abs < 86400) return `in ${Math.floor(abs / 3600)}h`;
    return `in ${Math.floor(abs / 86400)}d`;
  }
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtCadence(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

const pillStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  background: bg,
  color,
  letterSpacing: '0.02em',
});

export default function ScheduledJobs() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [runsFor, setRunsFor] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // Pending-approval filter + approval modal state (Phase 16k)
  const [onlyPendingApproval, setOnlyPendingApproval] = useState(false);
  const [approveJob, setApproveJob] = useState<JobRow | null>(null);
  const [approveTicket, setApproveTicket] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(JOBS_API);
      if (res.ok) {
        const d = await res.json();
        setJobs(d.jobs || []);
      } else {
        showToast(`Failed to load: HTTP ${res.status}`, false);
      }
    } catch (e) {
      console.error(e);
      showToast('Failed to load jobs', false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingApprovalCount = useMemo(
    () => jobs.filter((j) => j.pending_approval === true).length,
    [jobs],
  );

  const visibleJobs = useMemo(
    () => (onlyPendingApproval ? jobs.filter((j) => j.pending_approval === true) : jobs),
    [jobs, onlyPendingApproval],
  );

  const handleToggle = async (job: JobRow) => {
    if (job.pending_approval === true) {
      showToast(
        `${job.job_key} is pending approval — use "Approve & Enable"`,
        false,
      );
      return;
    }
    const next = !(job.enabled ?? job.default_enabled);
    const res = await authFetch(`${JOBS_API}/${job.job_key}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    });
    if (res.ok) {
      showToast(`${job.job_key}: ${next ? 'enabled' : 'disabled'}`, true);
      load();
    } else {
      showToast(`Toggle failed: HTTP ${res.status}`, false);
    }
  };

  const handleRunNow = async (job: JobRow) => {
    if (job.handler_is_stub) {
      showToast(`${job.job_key} is a Phase ${job.owner_phase} stub — cannot run yet`, false);
      return;
    }
    const body: Record<string, unknown> = {};
    if (job.per_org) {
      const input = window.prompt('org_id to run against (numeric):', '1');
      if (!input) return;
      const parsed = parseInt(input, 10);
      if (Number.isNaN(parsed)) {
        showToast('Invalid org_id', false);
        return;
      }
      body.org_id = parsed;
    }
    const res = await authFetch(`${JOBS_API}/${job.job_key}/run-now`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      showToast(`${job.job_key}: fired`, true);
      load();
    } else {
      const err = await res.text();
      showToast(`Run-now failed: ${err}`, false);
    }
  };

  const handleShowRuns = async (jobKey: string) => {
    setRunsFor(jobKey);
    setRunsLoading(true);
    setRuns([]);
    try {
      const res = await authFetch(`${JOBS_API}/${jobKey}/runs?limit=100`);
      if (res.ok) {
        const d = await res.json();
        setRuns(d.runs || []);
      }
    } catch (e) { console.error(e); }
    setRunsLoading(false);
  };

  const openApproveModal = (job: JobRow) => {
    setApproveJob(job);
    setApproveTicket('');
    setApproveError(null);
  };

  const closeApproveModal = () => {
    setApproveJob(null);
    setApproveTicket('');
    setApproveError(null);
    setApproveSubmitting(false);
  };

  const submitApproveEnable = async () => {
    if (!approveJob) return;
    const ticket = approveTicket.trim();
    if (!ticket) {
      setApproveError('Change-ticket reference is required.');
      return;
    }
    setApproveSubmitting(true);
    setApproveError(null);
    try {
      // 16k server-side contract: POST /toggle with enabled=true +
      // change_ticket_ref enforces the PCAOB AS 2201.A10-A12 ITGC
      // change-management gate for stub->real transitions. A bare
      // toggle without ticket is rejected with HTTP 422 when the job
      // is pending_approval=true.
      const res = await authFetch(
        `${JOBS_API}/${approveJob.job_key}/toggle`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: true, change_ticket_ref: ticket }),
        },
      );
      if (res.ok) {
        showToast(`${approveJob.job_key}: approved & enabled`, true);
        closeApproveModal();
        load();
      } else {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body && typeof body === 'object' && 'detail' in body) {
            detail = String((body as { detail: unknown }).detail);
          }
        } catch {
          /* non-JSON error body */
        }
        setApproveError(`Approval failed: ${detail}`);
      }
    } catch (e) {
      console.error(e);
      setApproveError('Approval failed: network error.');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const columns: Column[] = [
    {
      key: 'job_key',
      label: 'Job',
      render: (_v: string, row: JobRow) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.job_key}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{row.description}</div>
        </div>
      ),
    },
    {
      key: 'owner_phase',
      label: 'Phase',
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span>,
    },
    {
      key: 'cadence_seconds',
      label: 'Cadence',
      align: 'right',
      render: (v: number) => fmtCadence(v),
    },
    {
      key: 'last_run_at',
      label: 'Last Run',
      render: (v: string | null, row: JobRow) => (
        <div>
          <div>{fmtRelative(v)}</div>
          {row.last_status && (
            <div style={{ fontSize: 11, color: STATUS_COLOR[row.last_status] || 'var(--text-secondary)' }}>
              {row.last_status}
              {row.consecutive_failures > 0 && ` (×${row.consecutive_failures})`}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'next_run_at',
      label: 'Next Run',
      render: (v: string | null) => fmtRelative(v),
    },
    {
      key: 'handler_is_stub',
      label: 'Handler',
      render: (_v, row: JobRow) =>
        row.handler_is_stub ? (
          <span style={pillStyle('rgba(100, 116, 139, 0.14)', 'var(--text-secondary)')}>
            STUB
          </span>
        ) : (
          <span style={pillStyle('rgba(34, 197, 94, 0.12)', 'var(--positive)')}>REAL</span>
        ),
    },
    {
      key: 'pending_approval',
      label: 'Approval',
      render: (_v, row: JobRow) => {
        if (row.pending_approval === true) {
          return (
            <span
              style={pillStyle('rgba(239, 68, 68, 0.14)', '#b91c1c')}
              title="Stub→real transition detected on the most recent deploy. Admin approval + change-ticket required before this job may run (COSO P11 / PCAOB AS 2201.A10-A12)."
            >
              {'*'} AWAITING APPROVAL
            </span>
          );
        }
        if (row.enabled_admin_overridden_at) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={pillStyle('rgba(100, 116, 139, 0.12)', 'var(--text-secondary)')}>
                Admin-approved {fmtRelative(row.enabled_admin_overridden_at)}
              </span>
              {row.last_change_ticket_ref && (
                <span
                  style={{
                    fontSize: 10,
                    color: 'var(--text-hint)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                  title="Change-ticket reference"
                >
                  {row.last_change_ticket_ref}
                </span>
              )}
            </div>
          );
        }
        return <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>—</span>;
      },
    },
    {
      key: 'enabled',
      label: 'State',
      render: (_v, row: JobRow) => {
        const effective = row.enabled ?? row.default_enabled;
        return (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600,
              background: effective ? 'rgba(34, 197, 94, 0.12)' : 'rgba(100, 116, 139, 0.12)',
              color: effective ? 'var(--positive)' : 'var(--text-secondary)',
            }}
          >
            {effective ? 'ENABLED' : 'DISABLED'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_v, row: JobRow) => {
        const isPending = row.pending_approval === true;
        const toggleTitle = isPending
          ? "Use 'Approve & Enable' first"
          : undefined;
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <ActionButton label="Runs" onClick={() => handleShowRuns(row.job_key)} variant="secondary" size="sm" />
            <ActionButton
              label="Run Now"
              onClick={() => handleRunNow(row)}
              variant="secondary"
              size="sm"
              disabled={row.handler_is_stub}
            />
            <ActionButton
              label={(row.enabled ?? row.default_enabled) ? 'Disable' : 'Enable'}
              onClick={() => handleToggle(row)}
              variant="secondary"
              size="sm"
              disabled={isPending}
              title={toggleTitle}
            />
            {isPending && (
              <ActionButton
                label="Approve & Enable"
                onClick={() => openApproveModal(row)}
                variant="danger"
                size="sm"
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <PageLayout
      title="Scheduled Jobs"
      subtitle="Phase 4.5 — SchedulerRegistry for recurring heartbeat jobs. Enable / disable / run-now / audit."
      actions={[{ label: 'Refresh', onClick: load }]}
    >
      <KpiGrid
        columns={4}
        items={[
          { label: 'Jobs registered', value: jobs.length, color: 'var(--text-primary)' },
          {
            label: 'Enabled',
            value: jobs.filter((j) => (j.enabled ?? j.default_enabled) === true).length,
            color: 'var(--positive)',
          },
          {
            label: 'Stubs',
            value: jobs.filter((j) => j.handler_is_stub).length,
            color: 'var(--text-secondary)',
          },
          {
            label: 'Awaiting approval',
            value: pendingApprovalCount,
            color: pendingApprovalCount > 0 ? '#b91c1c' : 'var(--text-primary)',
            subtitle: pendingApprovalCount > 0 ? 'click to filter' : undefined,
            onClick: pendingApprovalCount > 0
              ? () => setOnlyPendingApproval((prev) => !prev)
              : undefined,
          },
        ]}
      />

      {pendingApprovalCount > 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 6,
            fontSize: 12,
            color: '#b91c1c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <strong>{pendingApprovalCount}</strong>{' '}
            job{pendingApprovalCount === 1 ? '' : 's'} awaiting admin approval.
            A handler was recently promoted from stub→real. Per COSO P11 /
            PCAOB AS 2201.A10-A12, admin approval + change-ticket reference
            is required before the job may run.
          </div>
          <button
            onClick={() => setOnlyPendingApproval((prev) => !prev)}
            style={{
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 6,
              border: '1px solid rgba(239, 68, 68, 0.3)',
              background: onlyPendingApproval ? '#b91c1c' : '#fff',
              color: onlyPendingApproval ? '#fff' : '#b91c1c',
              cursor: 'pointer',
            }}
          >
            {onlyPendingApproval ? 'Show all jobs' : 'Show only pending'}
          </button>
        </div>
      )}

      <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
        {visibleJobs.length} of {jobs.length} jobs shown
        {jobs.some((j) => j.enabled === null) && (
          <> · some not yet materialized in DB (scheduler not started or flag off)</>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={visibleJobs}
        loading={loading}
        emptyMessage={
          onlyPendingApproval
            ? 'No jobs currently awaiting approval.'
            : 'No scheduled jobs registered.'
        }
        rowKey="job_key"
      />

      {runsFor && (
        <Modal
          open
          onClose={() => {
            setRunsFor(null);
            setRuns([]);
          }}
          title={`Recent runs — ${runsFor}`}
          size="lg"
        >
          {runsLoading ? (
            <div style={{ padding: 20 }}>Loading…</div>
          ) : runs.length === 0 ? (
            <div style={{ padding: 20 }}>No runs recorded yet.</div>
          ) : (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <th style={{ textAlign: 'left', padding: 6 }}>Started</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Org</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Status</th>
                    <th style={{ textAlign: 'right', padding: 6 }}>Duration</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Source</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: 6 }}>{fmtRelative(r.started_at)}</td>
                      <td style={{ padding: 6 }}>{r.org_id ?? '—'}</td>
                      <td style={{ padding: 6, color: STATUS_COLOR[r.status] }}>{r.status}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>
                        {r.duration_ms != null ? `${r.duration_ms}ms` : '—'}
                      </td>
                      <td style={{ padding: 6, fontSize: 11 }}>{r.trigger_source}</td>
                      <td style={{ padding: 6, fontSize: 11, color: 'var(--negative)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.error_trace ? r.error_trace.split('\n').slice(-2).join(' ') : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {approveJob && (
        <Modal
          open
          onClose={() => {
            if (!approveSubmitting) closeApproveModal();
          }}
          title={`Approve & enable — ${approveJob.job_key}`}
          description="PCAOB AS 2201.A10-A12 ITGC change-management gate"
          size="md"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                padding: 12,
                background: 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
              }}
            >
              This handler was recently promoted from stub to a real
              implementation (Phase 16k). Per COSO P11 change management,
              admin approval plus a change-ticket reference is required
              before the job may run. Jobs must not auto-enable on deploy.
            </div>

            <div>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-hint)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 4,
                  display: 'block',
                }}
              >
                Change-ticket reference
              </label>
              <input
                autoFocus
                type="text"
                placeholder="e.g. CHG-1234 or JIRA-5678"
                value={approveTicket}
                onChange={(e) => {
                  setApproveTicket(e.target.value);
                  if (approveError) setApproveError(null);
                }}
                maxLength={80}
                disabled={approveSubmitting}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 13,
                  border: `1px solid ${approveError ? '#b91c1c' : 'var(--border-default)'}`,
                  borderRadius: 6,
                  background: '#fff',
                }}
              />
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-hint)',
                  marginTop: 4,
                }}
              >
                Jira / ServiceNow / CHG ticket. Required — this reference is
                persisted on the scheduled_jobs row as{' '}
                <code>last_change_ticket_ref</code>.
              </div>
            </div>

            {approveError && (
              <div
                style={{
                  padding: 10,
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#b91c1c',
                }}
              >
                {approveError}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                paddingTop: 6,
              }}
            >
              <ActionButton
                label="Cancel"
                onClick={closeApproveModal}
                variant="secondary"
                size="md"
                disabled={approveSubmitting}
              />
              <ActionButton
                label={approveSubmitting ? 'Submitting…' : 'Approve & Enable'}
                onClick={submitApproveEnable}
                variant="primary"
                size="md"
                disabled={approveSubmitting || !approveTicket.trim()}
              />
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 2000,
            padding: '12px 20px',
            borderRadius: 8,
            background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)',
            color: toast.ok ? 'var(--positive)' : 'var(--negative)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {toast.msg}
        </div>
      )}
    </PageLayout>
  );
}
